import {
  classes,
  prefetchSiteModules,
  ROOT_EQUIPMENT_STATUS_MAP,
  RootEquipmentStatus,
  RootEquipmentData,
  SiteGroup,
} from "@abb-connect/react-shared";
import { Marker } from "@adamscybot/react-leaflet-component-marker";
import DoughnutChart from "@components/DoughnutChart";
import SiteLink from "@components/SiteLink";
import { countByStatus } from "@services/status-service";
import { useQueryClient } from "@tanstack/react-query";
import { useRef } from "react";
import { Popup } from "react-leaflet";
import styles from "./SitePopup.module.css";

type SitePopupProps = {
  plantGroup: SiteGroup;
};

function SitePopup({ plantGroup }: SitePopupProps) {
  const markerRef = useRef<L.Marker>(null);
  const queryClient = useQueryClient();

  const onPlantHovered = async (site: string) => {
    await prefetchSiteModules(queryClient, site);
  };

  const statusCount: number[] = countByStatus(
    plantGroup.plants.flatMap((p) => p.rootEquipment.map((re) => re.status))
  );

  const totalRootEquipment = statusCount.reduce(
    (total, count) => total + count,
    0
  );

  const chartColors = Array.from(ROOT_EQUIPMENT_STATUS_MAP.values()).map(
    (status) => status.color
  );

  return (
    <Marker
      position={[plantGroup.latitude, plantGroup.longitude]}
      ref={markerRef}
      eventHandlers={{
        mouseover: () => {
          markerRef.current?.openPopup();
        },
      }}
      icon={
        <DoughnutChart
          data={statusCount}
          colors={chartColors}
          text={totalRootEquipment.toString()}
          size={32}
          title={"Root equipment status"}
        />
      }
    >
      <Popup closeButton={false} maxHeight={300}>
        <div className={styles["popup-content"]}>
          {plantGroup.plants.map((p) => {
            const areas = new Map<string | undefined, RootEquipmentData[]>();
            for (const equipment of p.rootEquipment) {
              const area =
                equipment.productionArea === ""
                  ? undefined
                  : (equipment.productionArea ?? undefined);
              const members = areas.get(area) ?? [];
              members.push(equipment);
              areas.set(area, members);
            }
            return (
              <div
                key={p.name}
                className={styles.site}
                onMouseEnter={() => {
                  void onPlantHovered(p.name);
                }}
              >
                <SiteLink sites={[p.name]}>
                  <div className={styles["site-title"]}>
                    <p>{p.name}</p>
                    <span className={`fi fi-${p.countryCode.toLowerCase()}`} />
                  </div>
                </SiteLink>
                {[...areas].map(([area, equipment]) => (
                  <SiteLink
                    key={area ?? ""}
                    sites={[p.name]}
                    productionArea={area}
                    className={styles["area-link"]}
                  >
                    {area && <p className={styles["area-title"]}>{area}</p>}
                    <div className={styles["root-equipment-container"]}>
                      {equipment.map((re) => {
                        const status = ROOT_EQUIPMENT_STATUS_MAP.get(
                          re.status ?? RootEquipmentStatus.NoDataConnection
                        )!;
                        return (
                          <div
                            key={re.name}
                            className={styles["root-equipment"]}
                          >
                            <div
                              className={classes(
                                styles.circle,
                                styles[status.class]
                              )}
                              style={{
                                color: status.color,
                                backgroundColor: status.color,
                              }}
                            />
                            <p>{re.name}</p>
                          </div>
                        );
                      })}
                    </div>
                  </SiteLink>
                ))}
              </div>
            );
          })}
        </div>
      </Popup>
    </Marker>
  );
}
export default SitePopup;
